import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { NotificationDto } from '@hrms/shared';
import { cn } from '@/lib/utils';
import { get, post } from '@/lib/api-client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const keys = {
  list: ['notifications'] as const,
  count: ['notifications', 'unread-count'] as const,
};

function ago(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;

  return new Date(iso).toLocaleDateString('en-PH', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Manila',
  });
}

/**
 * The bell.
 *
 * The count is polled rather than pushed. A websocket for this would be a
 * second transport to keep alive and re-authenticate for something nobody
 * needs within the minute; a cheap count endpoint every sixty seconds answers
 * the same question. The list is only fetched when somebody opens the panel.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: count } = useQuery({
    queryKey: keys.count,
    queryFn: () => get<{ unread: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
    // A failure here must not surface: the bell is ambient, and an error toast
    // every minute over a dropped request would be worse than a stale number.
    retry: false,
  });

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: keys.list,
    queryFn: () => get<NotificationDto[]>('/notifications'),
    enabled: open,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: keys.list });
    void queryClient.invalidateQueries({ queryKey: keys.count });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => post(`/notifications/${id}/read`, {}),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: () => post('/notifications/read-all', {}),
    onSuccess: invalidate,
  });

  const unread = count?.unread ?? 0;

  const openNotification = (notification: NotificationDto) => {
    if (!notification.readAt) markRead.mutate(notification.id);

    if (notification.link) {
      setOpen(false);
      void navigate(notification.link);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        onClick={() => setOpen(true)}
      >
        <Bell />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Notifications
              {unread > 0 && <Badge variant="secondary">{unread} unread</Badge>}
            </DialogTitle>
            <DialogDescription>
              Approvals, releases and anything else waiting on you.
            </DialogDescription>
          </DialogHeader>

          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-fit"
              onClick={() => markAllRead.mutate()}
              loading={markAllRead.isPending}
            >
              Mark all read
            </Button>
          )}

          <div className="max-h-[60vh] overflow-y-auto">
            {isLoading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
            ) : notifications.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing yet. Approvals and releases will appear here.
              </p>
            ) : (
              <ul className="divide-y">
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => openNotification(notification)}
                      className={cn(
                        'w-full rounded-md px-2 py-3 text-left transition-colors hover:bg-accent',
                        !notification.readAt && 'bg-accent/40',
                      )}
                    >
                      <p className="flex items-center gap-2 text-sm font-medium">
                        {!notification.readAt && (
                          <span
                            aria-hidden="true"
                            className="size-1.5 shrink-0 rounded-full bg-primary"
                          />
                        )}
                        {notification.title}
                      </p>
                      <p className="text-sm text-muted-foreground">{notification.body}</p>
                      <p className="text-xs text-muted-foreground">{ago(notification.createdAt)}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
